import type { Timeframe } from "@/api/types";

/**
 * SINGLE SOURCE OF TRUTH for timeframe metadata + load policy.
 *
 * Everything timeframe-related — display labels, seconds-per-bar, the
 * HTF/intraday split, and the initial fetch size — lives here so the
 * values can't drift between Chart, TradeJournal, Header, the ohlc
 * slice, etc. (They used to be copy-pasted in each, which is how the
 * "HTF chart missing" / "seconds out of sync" bugs kept recurring.)
 *
 * Add a new timeframe in ONE place: the TIMEFRAMES table below.
 *
 * Note: tick→bar *bucketing* (calendar arithmetic for week/month/year
 * boundaries) intentionally stays in its own focused module
 * `hooks/barBucket.ts` — it's not duplicated, just complex.
 */

export interface TimeframeMeta {
  value: Timeframe;
  /** Compact pill / in-chart label, e.g. "1h", "4h", "1D". */
  short: string;
  /** Full dropdown label, e.g. "1 Hour". */
  long: string;
  /** MT5 spelling, e.g. "H1", "M15". Used to label a screenshot with
   *  the chart it came from ("#H1-2") — MT5's vocabulary rather than
   *  this app's `value`, because that is what the user reads in the
   *  terminal all day. Display everywhere else still uses `short`. */
  mt5: string;
  /** Approximate seconds per bar. Calendar TFs (1M/1Y) are nominal —
   *  used for span math, whitespace sizing, jump-window sizing. */
  seconds: number;
  /** True for higher-timeframe buckets served WHOLE from htf.json
   *  (no date-window anchoring, no lazy-load). See isHtfTimeframe. */
  htf: boolean;
}

export const TIMEFRAMES: readonly TimeframeMeta[] = [
  { value: "1min", short: "1m", long: "1 Minute", seconds: 60, htf: false, mt5: "M1" },
  { value: "5min", short: "5m", long: "5 Minutes", seconds: 300, htf: false, mt5: "M5" },
  { value: "15min", short: "15m", long: "15 Minutes", seconds: 900, htf: false, mt5: "M15" },
  { value: "30min", short: "30m", long: "30 Minutes", seconds: 1800, htf: false, mt5: "M30" },
  { value: "1H", short: "1h", long: "1 Hour", seconds: 3600, htf: false, mt5: "H1" },
  { value: "2H", short: "2h", long: "2 Hours", seconds: 7200, htf: false, mt5: "H2" },
  { value: "4H", short: "4h", long: "4 Hours", seconds: 14400, htf: false, mt5: "H4" },
  { value: "1D", short: "1D", long: "1 Day", seconds: 86400, htf: true, mt5: "D1" },
  { value: "1W", short: "1W", long: "1 Week", seconds: 604800, htf: true, mt5: "W1" },
  { value: "1M", short: "1M", long: "1 Month", seconds: 2592000, htf: true, mt5: "MN1" },
  { value: "1Y", short: "1Y", long: "1 Year", seconds: 31536000, htf: true, mt5: "Y1" },
];

const BY_VALUE: Record<string, TimeframeMeta> = Object.fromEntries(
  TIMEFRAMES.map((t) => [t.value, t]),
);

/** Seconds-per-bar map. Kept as a Record for existing index-style call
 *  sites (`TIMEFRAME_SECONDS[tf] ?? 60`); prefer `tfSeconds()`. */
export const TIMEFRAME_SECONDS: Record<Timeframe, number> = Object.fromEntries(
  TIMEFRAMES.map((t) => [t.value, t.seconds]),
) as Record<Timeframe, number>;

/** Seconds per bar, with a safe fallback for unknown strings. */
export function tfSeconds(tf: string): number {
  return BY_VALUE[tf]?.seconds ?? 60;
}

/** Compact label ("1h", "1D"); falls back to the raw value. */
export function tfShortLabel(tf: string): string {
  return BY_VALUE[tf]?.short ?? tf;
}

/** MT5 spelling ("H1", "M15") for a screenshot's label prefix.
 *
 *  Falls back to the raw value rather than a generic placeholder: an
 *  unrecognised timeframe still names the chart correctly, which is
 *  all the label is for. Callers with no chart at all pass no prefix
 *  and the server applies its own default. */
export function tfMt5Label(tf: string): string {
  return BY_VALUE[tf]?.mt5 ?? tf;
}

// ---------------------------------------------------------------
// HTF vs intraday load policy — see issue #78, "HTF loads completely
// differently from intraday". HTF (1D/1W/1M/1Y) loads the FULL series
// from htf.json; intraday loads windowed per-day files with year/month
// anchoring + backward lazy-load. Anything that windows/paginates OHLC
// by date MUST branch on isHtfTimeframe().
// ---------------------------------------------------------------

export const HTF_TIMEFRAMES: readonly Timeframe[] = TIMEFRAMES.filter(
  (t) => t.htf,
).map((t) => t.value);

export function isHtfTimeframe(tf: string): boolean {
  return BY_VALUE[tf]?.htf ?? false;
}

/** Per-day files to fetch on a fresh INTRADAY load. Coarser intraday
 *  TFs (2H/4H) need more days up front to fill the screen; HTF ignores
 *  this (htf.json returns everything). Sized so the initial pull lands
 *  in the same ballpark of bars: 1H 30d≈720, 2H 60d≈720, 4H 180d≈1080. */
export function initialMaxFiles(tf: string): number {
  if (tf === "4H") return 180;
  if (tf === "2H") return 60;
  return 30;
}
